import React from 'react';
import Seo, { breadcrumbSchema } from '@/components/Seo';
import { CallLink, LeadForm } from '@/components/sections';
import { PHONE_DISPLAY, IMAGES } from '@/lib/site';
import { Phone, MapPin, Clock, CheckCircle2 } from 'lucide-react';

export default function ContactPage() {
  return (
    <>
      <Seo
        title="Contact for Water Damage Help | Tri-Cities BC"
        description="Request water damage help in Coquitlam, Port Coquitlam or Port Moody. Use the short form or call for an active water emergency."
        schema={breadcrumbSchema([{ name: 'Home', path: '/' }, { name: 'Contact', path: '/contact' }])}
      />
      <section className="relative overflow-hidden bg-navy">
        <img src={IMAGES.tech} alt="Water damage help and restoration response" className="absolute inset-0 h-full w-full object-cover opacity-50" loading="eager" fetchPriority="high" />
        <div className="absolute inset-0 bg-gradient-to-r from-navy-deep/90 via-navy-deep/75 to-navy/25" />
        <div className="container-page relative py-16">
          <span className="inline-block rounded-full bg-water/10 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-water ring-1 ring-water/20">Contact</span>
          <h1 className="mt-4 max-w-3xl font-display text-4xl font-extrabold text-white sm:text-5xl">Request water damage help</h1>
          <p className="mt-4 max-w-2xl text-lg leading-relaxed text-white/70">Use the short form to describe what happened. For active water that is still spreading, use the call option.</p>
        </div>
      </section>

      <section className="py-16">
        <div className="container-page grid gap-10 lg:grid-cols-[.9fr_1.1fr] lg:items-start">
          <div>
            <div className="rounded-3xl bg-secondary p-7 sm:p-8">
              <h2 className="font-display text-2xl font-extrabold text-primary">Before you submit</h2>
              <ul className="mt-6 space-y-4 text-sm leading-relaxed text-primary/85">
                <li className="flex gap-3"><CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-water-strong" /> If it is safe, stop the water source before doing anything else.</li>
                <li className="flex gap-3"><CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-water-strong" /> Avoid standing water near electrical outlets, appliances or damaged wiring.</li>
                <li className="flex gap-3"><CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-water-strong" /> Keep children and pets away from sewage or potentially contaminated water.</li>
              </ul>
            </div>

            <div className="mt-6 rounded-2xl border border-border bg-white p-6">
              <div className="flex items-start gap-3">
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-water-light text-water-strong"><Phone className="h-5 w-5" /></span>
                <div>
                  <div className="text-xs uppercase tracking-wider text-muted-foreground">Active emergency</div>
                  <div className="mt-3"><CallLink label={`CALL ${PHONE_DISPLAY}`} source="contact-number" /></div>
                  <p className="mt-3 text-sm leading-relaxed text-muted-foreground">Use the phone option when water is actively spreading or the situation cannot wait for a form response.</p>
                </div>
              </div>
            </div>

            <div className="mt-4 grid gap-4 sm:grid-cols-2">
              <div className="rounded-2xl border border-border bg-white p-5">
                <Clock className="h-5 w-5 text-water-strong" />
                <div className="mt-3 text-xs uppercase tracking-wider text-muted-foreground">Requests</div>
                <div className="mt-1 font-semibold text-primary">Accepted 24/7</div>
              </div>
              <div className="rounded-2xl border border-border bg-white p-5">
                <MapPin className="h-5 w-5 text-water-strong" />
                <div className="mt-3 text-xs uppercase tracking-wider text-muted-foreground">Service Area</div>
                <div className="mt-1 text-sm font-semibold text-primary">Coquitlam • Port Coquitlam • Port Moody</div>
              </div>
            </div>
          </div>

          <LeadForm source="contact-page" />
        </div>
      </section>
    </>
  );
}
